import React, { useEffect, useState } from "react";
import { useProjectManagementContext } from "../context";
import { CheckCircle2, Plus, Trash2, Wallet } from "lucide-react";
import { isInternalManproProject } from "../isInternalManproProject";

const TermijnSetup = () => {
  const { baselineData, setBaselineData, isBaselineLocked, activeProjectData } = useProjectManagementContext();
  const isInternalProject = isInternalManproProject(activeProjectData);
  const isLocked = Boolean(isBaselineLocked) || baselineData.status === 'finalized' || baselineData.status === 'pending_owner_approval' || baselineData.status === 'pending_rab_approval';

  const totalRAB = activeProjectData?.budget 
    ? Number(activeProjectData.budget) 
    : (baselineData?.budget ? Number(baselineData.budget) : 0);
  const paymentType = baselineData?.payment_type || 'full';
  
  const [formRows, setFormRows] = useState<{ title: string; percentage: number | string; amount: number | string }[]>(
    baselineData?.termijns && baselineData.termijns.length > 0 
      ? baselineData.termijns 
      : [
          { title: "DP (Uang Muka)", percentage: 30, amount: totalRAB * 0.3 },
          { title: "Pelunasan", percentage: 70, amount: totalRAB * 0.7 },
        ]
  );

  useEffect(() => {
    if (paymentType === 'termijn') {
      setBaselineData({ ...baselineData, termijns: formRows });
    } else {
      setBaselineData({ ...baselineData, termijns: [] });
    }
  }, [formRows, paymentType]);

  const setPaymentType = (type: 'full' | 'termijn') => {
    if (isLocked) return;
    setBaselineData({ ...baselineData, payment_type: type });
  };

  const currentTotalPercentage = formRows.reduce((acc, row) => acc + (Number(row.percentage) || 0), 0);
  const currentTotalAmount = formRows.reduce((acc, row) => acc + (Number(row.amount) || 0), 0);

  const addRow = () => {
    if (isLocked) return;
    setFormRows([...formRows, { title: "", percentage: 0, amount: 0 }]);
  };

  const removeRow = (index: number) => {
    if (isLocked) return;
    setFormRows(formRows.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: "title" | "percentage" | "amount", value: string | number) => {
    if (isLocked) return;
    const newRows = [...formRows];
    
    if (field === "title") {
      newRows[index].title = String(value);
    } 
    else if (field === "percentage") {
      const rawPct = String(value);
      let numPct = parseFloat(rawPct) || 0;
      if (numPct < 0) numPct = 0;
      if (numPct > 100) numPct = 100;
      
      const newAmt = totalRAB > 0 ? Math.round((numPct / 100) * totalRAB) : 0;
      newRows[index] = { ...newRows[index], percentage: numPct, amount: newAmt };
    } 
    else if (field === "amount") {
      const rawAmt = typeof value === 'string' ? value.replace(/\D/g, '') : value;
      let numAmt = parseFloat(String(rawAmt)) || 0;
      if (numAmt < 0) numAmt = 0;
      if (totalRAB > 0 && numAmt > totalRAB) numAmt = totalRAB;

      const newPct = totalRAB > 0 ? Math.round((numAmt / totalRAB) * 10000) / 100 : 0;
      newRows[index] = { ...newRows[index], amount: numAmt, percentage: newPct };
    }

    setFormRows(newRows);
  };

  const formatRupiah = (val: string | number) => {
    if (!val) return "";
    return Number(val).toLocaleString('id-ID');
  };

  if (isInternalProject) {
    return null;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-emerald-50 rounded-xl">
          <Wallet className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-800">Pengaturan Keuangan & Escrow</h3>
          <p className="text-sm text-slate-500">Tentukan cara pembayaran untuk RAB sebesar Rp {totalRAB.toLocaleString('id-ID')}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <button
          onClick={() => setPaymentType('full')}
          disabled={isLocked}
          className={`flex-1 p-4 rounded-xl border-2 text-left transition-all ${
            paymentType === 'full' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-200'
          } ${isLocked ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          <div className="font-bold text-slate-800 mb-1 flex items-center justify-between">
            Pembayaran Penuh Sekaligus
            {paymentType === 'full' && <CheckCircle2 className="text-blue-500" size={20} />}
          </div>
          <p className="text-xs text-slate-500">
            Klien mentransfer 100% nilai proyek di awal. Cocok untuk proyek kecil & menengah.
          </p>
        </button>
        
        <button
          onClick={() => setPaymentType('termijn')}
          disabled={isLocked}
          className={`flex-1 p-4 rounded-xl border-2 text-left transition-all ${
            paymentType === 'termijn' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-200'
          } ${isLocked ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          <div className="font-bold text-slate-800 mb-1 flex items-center justify-between">
            Pembayaran Bertahap (Termijn)
            {paymentType === 'termijn' && <CheckCircle2 className="text-blue-500" size={20} />}
          </div>
          <p className="text-xs text-slate-500">
            Pecah pembayaran menjadi beberapa termin (Misal: DP 30%, Pelunasan 70%).
          </p>
        </button>
      </div>

      {paymentType === 'termijn' && (
        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 mb-2 shadow-inner">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b border-slate-200 pb-4 gap-4">
            <div>
              <h4 className="font-bold text-slate-800 text-lg">Rincian Pembayaran Bertahap</h4>
              <p className="text-xs text-slate-500 mt-1">Ubah nama termin, persentase, atau nominal uangnya.</p>
            </div>
            <div className="flex flex-col gap-2">
              <div className={`px-4 py-2 rounded-xl text-sm font-black shadow-sm ${Math.round(currentTotalPercentage * 100) / 100 === 100 ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                Total Persen: {Math.round(currentTotalPercentage * 100) / 100}% / 100%
              </div>
              <div className={`px-4 py-2 rounded-xl text-sm font-black shadow-sm ${currentTotalAmount === totalRAB ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                Total Uang: Rp {currentTotalAmount.toLocaleString('id-ID')}
              </div>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-4 px-2 mb-3 text-xs font-bold text-slate-500 uppercase tracking-wider">
            <div className="flex-1">Nama Termin</div>
            <div className="w-32">Persentase (%)</div>
            <div className="w-48">Nominal (Rp)</div>
            <div className="w-10"></div>
          </div>

          <div className="space-y-4">
            {formRows.map((row, index) => (
              <div key={index} className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 p-4 md:p-2 bg-white md:bg-transparent rounded-xl border border-slate-200 md:border-transparent shadow-sm md:shadow-none">
                
                <div className="flex-1 relative">
                  <label className="md:hidden text-xs font-bold text-slate-500 mb-1 block">Nama Termin</label>
                  <input
                    type="text"
                    disabled={isLocked}
                    className="w-full text-sm border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:border-blue-400 transition-colors py-2.5 px-3 disabled:bg-slate-100 disabled:text-slate-500"
                    placeholder="Misal: Termin 1 (Pondasi)"
                    value={row.title}
                    onChange={(e) => updateRow(index, "title", e.target.value)}
                  />
                </div>

                <div className="w-full md:w-32 relative">
                  <label className="md:hidden text-xs font-bold text-slate-500 mb-1 block">Persentase (%)</label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      disabled={isLocked}
                      className="w-full text-sm font-bold text-blue-700 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm hover:border-blue-400 transition-colors py-2.5 pl-4 pr-8 text-center disabled:bg-slate-100 disabled:text-slate-500"
                      value={row.percentage}
                      onChange={(e) => updateRow(index, "percentage", e.target.value)}
                    />
                    <span className="absolute right-3 top-2.5 text-slate-400 font-bold text-sm">%</span>
                  </div>
                </div>

                <div className="w-full md:w-48 relative">
                  <label className="md:hidden text-xs font-bold text-slate-500 mb-1 block">Nominal (Rp)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-500 font-bold text-sm">Rp</span>
                    <input
                      type="text"
                      disabled={isLocked}
                      className="w-full text-sm font-bold text-green-700 border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm hover:border-green-400 transition-colors py-2.5 pl-9 pr-3 text-right disabled:bg-slate-100 disabled:text-slate-500"
                      value={formatRupiah(row.amount)}
                      onChange={(e) => updateRow(index, "amount", e.target.value)}
                    />
                  </div>
                </div>

                <div className="w-full md:w-10 flex justify-end md:justify-center mt-2 md:mt-0">
                  <button 
                    onClick={() => removeRow(index)} 
                    disabled={isLocked}
                    className="text-red-500 bg-red-50 hover:bg-red-500 hover:text-white p-2.5 rounded-lg transition-colors border border-red-100 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Hapus baris ini"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

              </div>
            ))}
          </div>

          {!isLocked && (
            <button 
              onClick={addRow} 
              className="mt-6 flex items-center justify-center gap-2 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 font-bold py-3 px-4 rounded-xl border border-blue-200 border-dashed w-full transition-colors"
            >
              <Plus size={18} strokeWidth={3} /> Tambah Baris Termin Baru
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TermijnSetup;
